import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BookOpen, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import MarkdownDocument from "@/components/MarkdownDocument";

/**
 * Iter 85.41 · Activity Story Digest panel.
 *
 * Renders inside the NoteViewerDialog when the current note is an
 * `activity_briefing`. Fetches the cached digest via
 *   GET  /api/notes/{activityId}/story-digest
 * and offers a `Generate` / `Regenerate` button that posts
 *   POST /api/notes/{activityId}/story-digest
 * which stitches every linked meeting note into a single markdown
 * activity story via Claude.
 *
 * Staleness UX
 * ────────────
 * The backend reports `fresh: false` and `pending_notes: N` when new
 * meeting notes have been linked after the last generation. In that
 * case the panel shows a subtle brass "N new meeting notes since the
 * last story" caption plus a "Regenerate" primary CTA so the partner
 * knows the current narrative is out of date.
 *
 * The digest itself is stored on the activity note under
 * `activity_story_digest = { story, generated_at, source_note_ids }`
 * so re-opening the activity is instant and doesn't re-cost the LLM.
 */
export default function ActivityStoryDigest({ activityId }) {
  const [state, setState] = useState({ loading: true, digest: null, pending: 0, linkedCount: 0 });
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/notes/${activityId}/story-digest`)
      .then((r) => {
        const d = r.data || {};
        setState({
          loading: false,
          digest: d.story ? d : null,
          pending: d.pending_notes || 0,
          linkedCount: (d.linked_note_ids || []).length,
        });
      })
      .catch(() => setState({ loading: false, digest: null, pending: 0, linkedCount: 0 }));
  };

  useEffect(() => { if (activityId) load(); }, [activityId]);

  const regenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/notes/${activityId}/story-digest`);
      setState({
        loading: false,
        digest: data,
        pending: 0,
        linkedCount: (data.source_note_ids || []).length,
      });
      toast.success("Activity story regenerated");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to generate story";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // Empty state — no linked meeting notes yet. Rendered as a quiet
  // dashed panel rather than hiding, so the mechanism is discoverable
  // and doesn't spring on the partner the moment they link a note.
  if (!state.loading && state.linkedCount === 0) {
    return (
      <div className="border border-dashed border-border p-4" data-testid="activity-story-empty">
        <div className="flex items-center gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">
          <BookOpen size={12} weight="bold" /> Activity story
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Link at least one meeting note to this activity to enable a stitched activity story.
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border" data-testid="activity-story-digest">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[0.6875rem] font-mono uppercase tracking-widest text-brass">
          <BookOpen size={12} weight="bold" /> Activity story
          {state.digest && !state.pending ? (
            <span className="text-muted-foreground normal-case tracking-normal text-[0.6875rem]">
              · fresh — {state.linkedCount} linked note{state.linkedCount === 1 ? "" : "s"}
            </span>
          ) : null}
          {state.digest && state.pending > 0 ? (
            <span className="text-brass normal-case tracking-normal text-[0.6875rem]">
              · {state.pending} new meeting note{state.pending === 1 ? "" : "s"} since last story
            </span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant={state.digest ? "outline" : "default"}
          className="rounded-none h-7 text-xs"
          onClick={regenerate}
          disabled={generating}
          data-testid="activity-story-generate"
        >
          <Sparkle size={12} weight="fill" className="mr-1" />
          {generating ? "Generating…" : state.digest ? "Regenerate" : "Generate story"}
        </Button>
      </div>
      {state.loading ? (
        <div className="px-4 py-4 text-xs text-muted-foreground">Loading activity story…</div>
      ) : state.digest?.story ? (
        <div className="px-4 py-4">
          <MarkdownDocument>{state.digest.story}</MarkdownDocument>
          {state.digest.generated_at && (
            <div className="mt-4 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">
              Last generated {new Date(state.digest.generated_at).toLocaleString()}
              {state.digest.generated_by ? ` · by ${state.digest.generated_by}` : ""}
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-4 text-xs text-muted-foreground italic">
          No story yet. Click "Generate story" to stitch the {state.linkedCount} linked meeting note{state.linkedCount === 1 ? "" : "s"} into a single activity narrative.
        </div>
      )}
    </div>
  );
}
